import React from 'react';
import { motion } from 'framer-motion';

interface SensorGaugeProps {
  title: string;
  value: number;
  max: number;
  unit: string;
  color: string;
  icon: React.ReactNode;
}

const SensorGauge: React.FC<SensorGaugeProps> = ({ title, value, max, unit, color, icon }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const circumference = 2 * Math.PI * 45;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold text-sm">{title}</h3>
        <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${color} flex items-center justify-center text-white`}>
          {icon}
        </div>
      </div>
      
      <div className="relative flex items-center justify-center">
        <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r="45"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="8"
            fill="none"
          />
          {/* Progress circle */}
          <motion.circle
            cx="50"
            cy="50"
            r="45"
            stroke="url(#gradient)"
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset }}
            transition={{ duration: 1, ease: "easeOut" }}
          />
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" className={`stop-color-${color.split(' ')[1]}`} />
              <stop offset="100%" className={`stop-color-${color.split(' ')[3]}`} />
            </linearGradient>
          </defs>
        </svg>
        
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-white">{value}</span>
          <span className="text-xs text-purple-300">{unit}</span>
        </div>
      </div>
      
      <div className="mt-2 text-center">
        <span className="text-xs text-purple-300">Max: {max}{unit}</span>
      </div>
    </div>
  );
};

export default SensorGauge;